import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Typography,
  Button,
  useTheme,
  Divider,
  Icon,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Language,
  LocationOn,
  Phone,
  SentimentDissatisfied,
  WhatsApp,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { publicService } from '../../shared/services';
import { PAYMENT_ICONS } from '../../shared/constants/paymentMethods';

/**
 * Sanitiza URLs de redes sociais para evitar open redirect.
 * - Instagram: só permite subdomínios de instagram.com
 * - Facebook: só permite subdomínios de facebook.com
 * - Website: só permite http/https (qualquer domínio — propósito geral)
 * Se não couber em nenhuma regra, retorna null (link não renderizado).
 */
function sanitizeSocialUrl(input: string | undefined | null, type: 'instagram' | 'facebook' | 'website'): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    if (type === 'instagram') {
      // Aceita: instagram.com/username, @username
      const clean = trimmed.replace(/^@/, '');
      // Se já é URL, verifica domínio
      if (clean.startsWith('http')) {
        const url = new URL(clean);
        if (url.hostname === 'instagram.com' || url.hostname.endsWith('.instagram.com')) {
          return clean;
        }
        return null; // domínio não permitido
      }
      // Assume username — monta URL segura
      return `https://instagram.com/${clean.replace(/[^a-zA-Z0-9_.]/g, '')}`;
    }

    if (type === 'facebook') {
      // Aceita: facebook.com/page, https://facebook.com/page
      if (trimmed.startsWith('http')) {
        const url = new URL(trimmed);
        if (url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com')) {
          return trimmed;
        }
        return null; // domínio não permitido
      }
      // Assume facebook.com/<handle>
      const clean = trimmed.replace(/^facebook\.com\//i, '');
      return `https://facebook.com/${clean.replace(/[^a-zA-Z0-9_.]/g, '')}`;
    }

    if (type === 'website') {
      // Website pode ser qualquer domínio, mas só http/https
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        new URL(trimmed); // valida formato
        return trimmed;
      }
      // Assume https prefix
      return `https://${trimmed}`;
    }

    return null;
  } catch {
    return null;
  }
}

function isBusinessOpen(
  schedule: { dayOfWeek: number; isClosed: boolean; startTime?: string | null; endTime?: string | null }[]
): { open: boolean; text: string } {
  const now = new Date();
  // Use the IANA timezone to get accurate Brazil time regardless of client locale
  const brtTime = now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo', hour12: false });
  const [datePart, timePart] = brtTime.split(', ');
  const [month, day, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  const currentDay = new Date(year, month - 1, day).getDay(); // 0=Sunday
  const currentMinutes = hours * 60 + minutes;

  const todaySchedule = schedule.find(s => s.dayOfWeek === currentDay);
  if (todaySchedule?.isClosed || !todaySchedule?.startTime || !todaySchedule?.endTime) {
    return { open: false, text: 'Fechado hoje' };
  }

  const [startH, startM] = todaySchedule.startTime.split(':').map(Number);
  const [endH, endM] = todaySchedule.endTime.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
    const remaining = endMinutes - currentMinutes;
    const h = Math.floor(remaining / 60);
    const m = remaining % 60;
    if (h > 0) return { open: true, text: `Aberto — fecha em ${h}h${m > 0 ? m : ''}` };
    return { open: true, text: `Aberto — fecha em ${m}min` };
  }

  if (currentMinutes < startMinutes) {
    return { open: false, text: `Abre hoje às ${todaySchedule.startTime}` };
  }

  return { open: false, text: 'Fechado' };
}

export const BusinessPublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['publicBusiness', slug],
    queryFn: () => publicService.getBySlug(slug!),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <SentimentDissatisfied fontSize="large" color="action" />
        <Typography variant="h6" color="text.secondary">Estabelecimento não encontrado</Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>Voltar para o início</Button>
      </Box>
    );
  }

  const { id, name, services, employees, weeklySchedules, address, profilePicture, phone, instagram, facebook, website, paymentMethods } = data;
  const schedule = weeklySchedules ?? [];
  const status = isBusinessOpen(schedule);

  const googleMapsUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null;

  const whatsappUrl = phone
    ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent('Olá! Gostaria de mais informações.')}`
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #121212 0%, #1e1e1e 100%)'
          : 'linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)',
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: '#fff',
          pt: 6,
          pb: 4,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          {profilePicture && (
            <Avatar
              src={profilePicture}
              alt={name}
              slotProps={{ img: { loading: 'lazy' } }}
              sx={{ width: 100, height: 100, mx: 'auto', mb: 2, border: '3px solid rgba(255,255,255,0.3)', imageRendering: 'auto' }}
            />
          )}
          <Typography variant="h3" fontWeight={700} gutterBottom>
            {name}
          </Typography>

          {/* Status Aberto/Fechado */}
          <Chip
            label={status.text}
            color={status.open ? 'success' : 'error'}
            variant="filled"
            size="small"
            sx={{ mb: 2, fontWeight: 600, color: '#fff' }}
          />

          {/* Address with Google Maps link */}
          {address && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
              <LocationOn fontSize="small" sx={{ opacity: 0.9 }} />
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {address}
                {googleMapsUrl && (
                  <Link
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'rgba(255,255,255,0.8)', ml: 0.5, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    (Ver Mapa)
                  </Link>
                )}
              </Typography>
            </Box>
          )}

          {/* Phone */}
          {phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
              <Phone fontSize="small" sx={{ opacity: 0.9 }} />
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {phone}
              </Typography>
            </Box>
          )}

          {/* CTA Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#ffffff',
                color: theme.palette.secondary.main,
                fontWeight: 'bold',
                borderRadius: 6,
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
              onClick={() => navigate(`/appointment/${id}`)}
            >
              Agendar Horário
            </Button>
          </Box>

          {/* Social Media + WhatsApp */}
          {(phone || instagram || facebook || website) && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              {phone && (
                <Tooltip title="WhatsApp" arrow>
                  <IconButton
                    component="a"
                    href={whatsappUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <WhatsApp />
                  </IconButton>
                </Tooltip>
              )}
              {sanitizeSocialUrl(instagram, 'instagram') && (
                <Tooltip title="Instagram" arrow>
                  <IconButton
                    component="a"
                    href={sanitizeSocialUrl(instagram, 'instagram')!}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Instagram />
                  </IconButton>
                </Tooltip>
              )}
              {sanitizeSocialUrl(facebook, 'facebook') && (
                <Tooltip title="Facebook" arrow>
                  <IconButton
                    component="a"
                    href={sanitizeSocialUrl(facebook, 'facebook')!}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Facebook />
                  </IconButton>
                </Tooltip>
              )}
              {sanitizeSocialUrl(website, 'website') && (
                <Tooltip title="Site" arrow>
                  <IconButton
                    component="a"
                    href={sanitizeSocialUrl(website, 'website')!}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Language />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Opening Hours */}
        {schedule && schedule.length > 0 && (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Horários de Funcionamento
            </Typography>
            <Card sx={{ p: 2, borderRadius: 2, mb: 4 }}>
              {[...schedule].sort((a, b) => a.dayOfWeek - b.dayOfWeek).map((day) => (
                <Box
                  key={day.dayOfWeek}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                    opacity: day.isClosed ? 0.5 : 1,
                  }}
                >
                  <Typography variant="body1" fontWeight={day.isClosed ? 400 : 500}>
                    {day.dayName}
                  </Typography>
                  <Typography variant="body1" color={day.isClosed ? 'error' : 'text.primary'}>
                    {day.isClosed ? 'Fechado' : day.startTime ? `${day.startTime.substring(0, 5)} às ${day.endTime?.substring(0, 5)}` : 'Fechado'}
                  </Typography>
                </Box>
              ))}
            </Card>
          </>
        )}

        {/* Payment Methods */}
        {paymentMethods && (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Formas de Pagamento
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap' }}>
              {paymentMethods.split(',').map((method) => {
                const m = method.trim();
                return (
                  <Chip
                    key={m}
                    label={m}
                    variant="outlined"
                    color="primary"
                    icon={<Icon>{PAYMENT_ICONS[m] || 'payments'}</Icon>}
                  />
                );
              })}
            </Box>
          </>
        )}

        <Divider sx={{ mb: 4 }} />

        {/* Services */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Serviços
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} key={service.id}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] },
                }}
              >
                <Typography variant="body1" fontWeight={500}>
                  {service.description}
                </Typography>
                <Chip
                  label={`R$ ${service.amount.toFixed(2).replace('.', ',')}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Card>
            </Grid>
          ))}
          {services.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" textAlign="center" py={2}>
                Nenhum serviço cadastrado ainda.
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Employees */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Profissionais
        </Typography>
        <Grid container spacing={2}>
          {employees.map((emp) => (
            <Grid item xs={12} sm={6} key={emp.id}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] },
                }}
              >
                <Avatar
                  src={emp.profilePicture || undefined}
                  slotProps={{ img: { loading: 'lazy' } }}
                  sx={{ bgcolor: emp.color || theme.palette.primary.main, width: 48, height: 48 }}
                >
                  {emp.name?.[0]?.toUpperCase() || 'P'}
                </Avatar>
                <Typography variant="body1" fontWeight={600}>
                  {emp.name}
                </Typography>
              </Card>
            </Grid>
          ))}
          {employees.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" textAlign="center" py={2}>
                Nenhum profissional cadastrado ainda.
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* CTA */}
        <Box textAlign="center" mt={6} mb={4}>
          <Button
            variant="contained"
            size="large"
            sx={{ px: 6, py: 1.5, borderRadius: 3, fontWeight: 700 }}
            onClick={() => navigate(`/appointment/${id}`)}
          >
            Agende seu Horário
          </Button>
        </Box>

        {/* Footer */}
        <Box textAlign="center" py={2}>
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()} SconApp — Gestão para barbearias e salões
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};